import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AgeVerification from '../components/AgeVerification';
import CookieConsent from '../components/CookieConsent';
import { Heart, Shield, Users, AlertTriangle, Phone, Clock, Target, BarChart } from 'lucide-react';

const ResponsibleGaming = () => {
  return (
    <div className="min-h-screen bg-book-cream">
      <Header />
      <main className="book-section">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="flex items-center justify-center space-x-4 mb-4">
              <div className="w-16 h-16 bg-book-brown rounded-full flex items-center justify-center">
                <Heart className="h-8 w-8 text-book-cream" />
              </div>
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-book-brown mb-2">
                  Responsible Gaming
                </h1>
                <p className="text-lg text-book-brown-dark">
                  Your well-being and enjoyment come first in everything we do
                </p>
              </div>
            </div>
            <div className="bg-book-beige rounded-lg p-4 text-sm text-book-brown-dark">
              <strong>Committed to Safe Gaming Since 2020</strong>
            </div>
          </div>

          {/* Introduction */}
          <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed mb-12">
            <p className="mb-6">
              At Brit Ember, responsible gaming represents our highest priority and fundamental commitment to our community.
              We believe that casino entertainment should enhance lives, not diminish them. Our comprehensive responsible
              gaming framework combines education, technology, and genuine care to ensure that every player can enjoy
              gaming safely and sustainably.
            </p>

            <p className="mb-6">
              Our approach goes beyond regulatory compliance to embrace a holistic philosophy that recognizes gambling
              as entertainment that should remain enjoyable, affordable, and under personal control. We provide tools,
              resources, and support to help players maintain healthy gaming habits while maximizing their entertainment value.
            </p>
          </div>

          {/* Main Content Sections */}
          <div className="space-y-12">
            {/* Section 1 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Shield className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Understanding Responsible Gaming</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Responsible gaming encompasses the principles and practices that ensure casino entertainment remains
                      a positive, controlled experience. It involves understanding the nature of gambling, recognizing
                      personal limits, and using tools and strategies to maintain healthy gaming habits.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Core Principles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Personal Control</h4>
                        <p className="text-sm text-book-brown-dark">You maintain complete control over your gaming decisions and spending</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Informed Choices</h4>
                        <p className="text-sm text-book-brown-dark">Understanding odds, probabilities, and gaming mechanics</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Balanced Lifestyle</h4>
                        <p className="text-sm text-book-brown-dark">Gaming as one part of a healthy, balanced life</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Financial Prudence</h4>
                        <p className="text-sm text-book-brown-dark">Gaming only with disposable income</p>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">The Reality of Gambling</h3>
                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
                      <p className="text-blue-800">
                        Gambling involves chance and risk. While it can be entertaining, it should never be viewed as
                        a way to make money. All casino games are designed with a mathematical house edge, meaning
                        the casino has a statistical advantage over time. Responsible gaming means accepting this
                        reality while enjoying the entertainment value.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Target className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Self-Assessment and Awareness</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Regular self-assessment represents the foundation of responsible gaming. Understanding your
                      motivations, recognizing warning signs, and maintaining awareness of your gaming patterns
                      enables you to enjoy casino entertainment safely and sustainably.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Warning Signs to Watch For</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Gaming with money needed for essential expenses</li>
                      <li>Chasing losses or trying to win back money</li>
                      <li>Gaming for longer than intended</li>
                      <li>Neglecting work, relationships, or hobbies due to gaming</li>
                      <li>Feeling anxious or depressed about gaming activities</li>
                      <li>Lying about gaming habits to others</li>
                      <li>Using gaming as an escape from problems</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Healthy Gaming Habits</h3>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Set and respect time and money limits before gaming</li>
                      <li>Take regular breaks during gaming sessions</li>
                      <li>Balance gaming with other recreational activities</li>
                      <li>Track your gaming activity and spending</li>
                      <li>Never game when stressed, tired, or under the influence</li>
                      <li>Use gaming as entertainment, not as a way to make money</li>
                      <li>Be aware of how gaming affects your mood and well-being</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Self-Assessment Questions</h3>
                    <div className="space-y-3 mb-4">
                      <div className="bg-book-beige rounded-lg p-4">
                        <p className="text-book-brown-dark text-sm">
                          <strong>Do you game to escape problems?</strong> If gaming becomes your primary coping mechanism,
                          it may indicate a need to address underlying issues.
                        </p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <p className="text-book-brown-dark text-sm">
                          <strong>Can you stop gaming when you want to?</strong> Loss of control over gaming behavior
                          represents a significant warning sign.
                        </p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <p className="text-book-brown-dark text-sm">
                          <strong>Does gaming affect your relationships?</strong> If gaming creates conflict or distance
                          in your personal relationships, it's time to reassess.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <BarChart className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Tools and Resources for Responsible Gaming</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Modern responsible gaming combines technological tools with educational resources to empower
                      players with control and awareness. We provide comprehensive tools and connect you with
                      professional support services to ensure your gaming remains enjoyable and sustainable.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Brit Ember Responsible Gaming Tools</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">Session Limits</h4>
                        <p className="text-green-700 text-sm">Set maximum gaming time per session with automatic reminders</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">Deposit Controls</h4>
                        <p className="text-green-700 text-sm">Configure daily, weekly, or monthly spending limits</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">Reality Checks</h4>
                        <p className="text-green-700 text-sm">Regular notifications about time spent and money used</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-4">
                        <h4 className="font-semibold text-green-800 mb-2">Activity Tracking</h4>
                        <p className="text-green-700 text-sm">Detailed reports of your gaming patterns and habits</p>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Casino Operator Tools</h3>
                    <p className="mb-4">
                      All licensed UK casino operators provide comprehensive responsible gaming tools. When registering
                      with any casino, you have access to:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li>Deposit limits and cooling-off periods</li>
                      <li>Self-exclusion options (temporary or permanent)</li>
                      <li>Loss limits and stake limits</li>
                      <li>Reality check reminders</li>
                      <li>Account activity statements</li>
                      <li>24/7 customer support for responsible gaming concerns</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <Users className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Support and Professional Help</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Professional support represents a crucial resource for anyone concerned about their gaming habits.
                      The UK provides comprehensive support services through both public organizations and private
                      counseling services. Seeking help represents strength, not weakness, and most people who seek
                      assistance successfully regain control of their gaming.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">UK Support Organizations</h3>
                    <div className="space-y-4 mb-4">
                      <div className="bg-red-50 border-l-4 border-red-400 p-4">
                        <h4 className="font-semibold text-red-800 mb-2">GamCare</h4>
                        <p className="text-red-700 text-sm mb-2">National center for information and advice on gambling</p>
                        <p className="text-red-700 text-sm"><strong>Helpline:</strong> 0808 8020 133 | <strong>Website:</strong> <a href="https://www.gamcare.org.uk" className="underline">gamcare.org.uk</a></p>
                      </div>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                        <h4 className="font-semibold text-blue-800 mb-2">Gordon House Association</h4>
                        <p className="text-blue-700 text-sm mb-2">Charitable organization providing counseling and support</p>
                        <p className="text-blue-700 text-sm"><strong>Helpline:</strong> 0151 933 4303 | <strong>Website:</strong> <a href="https://www.gordonhouse.org.uk" className="underline">gordonhouse.org.uk</a></p>
                      </div>
                      <div className="bg-green-50 border-l-4 border-green-400 p-4">
                        <h4 className="font-semibold text-green-800 mb-2">Gamblers Anonymous</h4>
                        <p className="text-green-700 text-sm mb-2">Peer support through mutual help meetings</p>
                        <p className="text-green-700 text-sm"><strong>Helpline:</strong> 08700 50 88 80 | <strong>Website:</strong> <a href="https://www.gamblersanonymous.org.uk" className="underline">gamblersanonymous.org.uk</a></p>
                      </div>
                      <div className="bg-purple-50 border-l-4 border-purple-400 p-4">
                        <h4 className="font-semibold text-purple-800 mb-2">BeGambleAware</h4>
                        <p className="text-purple-700 text-sm mb-2">Educational resources and responsible gambling information</p>
                        <p className="text-purple-700 text-sm"><strong>Website:</strong> <a href="https://www.begambleaware.org" className="underline">begambleaware.org</a></p>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">When to Seek Help</h3>
                    <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                      <h4 className="font-semibold text-yellow-800 mb-2">Warning Signs Requiring Professional Support</h4>
                      <ul className="text-yellow-800 text-sm space-y-1">
                        <li>• Gaming has become the most important activity in your life</li>
                        <li>• You're gaming with money needed for essential living expenses</li>
                        <li>• You're experiencing financial, emotional, or relationship problems due to gaming</li>
                        <li>• Failed attempts to control or stop gaming</li>
                        <li>• Feeling anxious, depressed, or suicidal about gaming</li>
                        <li>• Using gaming as an escape from other problems</li>
                        <li>• Lying about gaming habits to family and friends</li>
                      </ul>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Emergency Support</h3>
                    <p className="mb-4">
                      If you're experiencing a gambling-related crisis or having thoughts of self-harm, immediate
                      professional help is available:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li><strong>Samaritans:</strong> 116 123 (24/7 emotional support)</li>
                      <li><strong>Emergency Services:</strong> 999 (life-threatening situations)</li>
                      <li><strong>NHS 111:</strong> 111 (non-emergency medical advice)</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 5 */}
            <div className="bg-white rounded-xl shadow-book p-8">
              <div className="flex items-start space-x-4 mb-6">
                <div className="flex-shrink-0 w-12 h-12 bg-book-brown rounded-lg flex items-center justify-center">
                  <AlertTriangle className="h-6 w-6 text-book-cream" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-book-brown mb-3">Prevention and Education</h2>
                  <div className="prose prose-lg max-w-none text-book-brown-dark leading-relaxed">
                    <p className="mb-4">
                      Prevention through education represents the most effective approach to responsible gaming.
                      Understanding the nature of gambling, developing healthy attitudes, and learning practical
                      strategies enables players to enjoy casino entertainment without risking harm.
                    </p>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Understanding Gambling Mathematics</h3>
                    <p className="mb-4">
                      All casino games operate on mathematical principles that favor the house over time. Understanding
                      these principles helps players make informed decisions:
                    </p>
                    <ul className="list-disc pl-6 space-y-2 mb-4">
                      <li><strong>House Edge:</strong> The mathematical advantage the casino has over players</li>
                      <li><strong>RTP (Return to Player):</strong> The percentage of wagers returned to players over time</li>
                      <li><strong>Variance:</strong> How results are distributed around the expected outcome</li>
                      <li><strong>Bankroll Management:</strong> Strategies for managing gaming budgets responsibly</li>
                    </ul>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Developing Healthy Gaming Habits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Set Clear Goals</h4>
                        <p className="text-sm text-book-brown-dark">Define why you game and what you hope to achieve</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Maintain Balance</h4>
                        <p className="text-sm text-book-brown-dark">Keep gaming as one part of a varied lifestyle</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Track Activity</h4>
                        <p className="text-sm text-book-brown-dark">Monitor time and money spent on gaming</p>
                      </div>
                      <div className="bg-book-beige rounded-lg p-4">
                        <h4 className="font-semibold text-book-brown mb-2">Know When to Stop</h4>
                        <p className="text-sm text-book-brown-dark">Respect predetermined limits and break points</p>
                      </div>
                    </div>

                    <h3 className="text-xl font-semibold text-book-brown mt-6 mb-3">Myths and Realities</h3>
                    <div className="space-y-3 mb-4">
                      <div className="bg-red-50 border-l-4 border-red-400 p-4">
                        <p className="text-red-800 text-sm">
                          <strong>Myth:</strong> "Past results predict future outcomes"
                        </p>
                        <p className="text-red-700 text-sm mt-1">
                          <strong>Reality:</strong> Each casino game outcome is independent and random
                        </p>
                      </div>
                      <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
                        <p className="text-blue-800 text-sm">
                          <strong>Myth:</strong> "You can beat the casino with skill alone"
                        </p>
                        <p className="text-blue-700 text-sm mt-1">
                          <strong>Reality:</strong> While skill matters in some games, the house always has a mathematical advantage
                        </p>
                      </div>
                      <div className="bg-green-50 border-l-4 border-green-400 p-4">
                        <p className="text-green-800 text-sm">
                          <strong>Myth:</strong> "Gaming is a reliable way to make money"
                        </p>
                        <p className="text-green-700 text-sm mt-1">
                          <strong>Reality:</strong> Gaming should be viewed as entertainment, not investment
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-book-beige rounded-xl p-8 shadow-book-inset mt-12">
            <h2 className="text-2xl font-bold text-book-brown mb-6 text-center">Need Support Right Now?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="font-semibold text-book-brown mb-2">GamCare Helpline</h3>
                <p className="text-book-brown-dark mb-1 font-bold">0808 8020 133</p>
                <p className="text-sm text-book-brown-dark">24/7 confidential support</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Clock className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-book-brown mb-2">Gordon House</h3>
                <p className="text-book-brown-dark mb-1 font-bold">0151 933 4303</p>
                <p className="text-sm text-book-brown-dark">Professional counseling</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Heart className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-book-brown mb-2">Samaritans</h3>
                <p className="text-book-brown-dark mb-1 font-bold">116 123</p>
                <p className="text-sm text-book-brown-dark">Emotional support</p>
              </div>
            </div>
            <div className="mt-6 text-center">
              <p className="text-sm text-book-brown-dark">
                Remember: Seeking help is a sign of strength. Professional support is available 24/7,
                and recovery is possible for anyone who wants it.
              </p>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <AgeVerification />
      <CookieConsent />
    </div>
  );
};

export default ResponsibleGaming;
